import { BufferAttribute, BufferGeometry, DoubleSide, Mesh, ShaderMaterial, Vector3 } from 'three';
import type { IUniform } from 'three';

import type { Palette } from 'shared/interfaces/planogram';
import { SphereItemType } from 'shared/interfaces/planogram';
import type { BaseObjectUserData } from 'shared/object3d/interfaces';
import type { CurveObjectConfig } from 'shared/interfaces/object_config';
import {
  curvePathFromPoints,
  getVertexNormal,
  simplifyCurvePoints,
} from 'shared/utils/curve-rendering-utils';
import { hexToRgbaArray, resolvePaletteColor, RgbaArray } from 'shared/utils/colors';
import curveFragmentShader from 'shared/renderingEngine/shaders/CurveFragmentShader.glsl';
import standardVertexShader from 'shared/renderingEngine/shaders/StandardVertexShader.glsl';

import BaseObject3D from 'shared/object3d/BaseObject3D';

interface CurveMaterialUnifroms {
  color: IUniform<RgbaArray>;
  dashed: IUniform<boolean>;
  soft: IUniform<boolean>;
  scale: IUniform<[number, number, number]>;
  thickness: IUniform<number>;
  curveLength: IUniform<number>;
}

interface CurveUserData extends BaseObjectUserData {
  mesh: Mesh;
}

class CurveObject3D extends BaseObject3D<CurveUserData> {
  constructor() {
    super();

    this.itemType = SphereItemType.Curve;
  }

  configure(curveConfig: CurveObjectConfig) {
    const geometry = CurveObject3D.updateGeometryAttributes(new BufferGeometry(), curveConfig);
    const curveLength = geometry.userData.curveLength as number | undefined;
    const material = CurveObject3D.generateMaterial(curveConfig, curveLength);
    const mesh = new Mesh(geometry, material);
    mesh.name = 'CurveMesh';

    this.userData.mesh = mesh;
    this.add(mesh);
  }

  // TODO: merge with configure
  rebuild(curveConfig: CurveObjectConfig, palette: Palette | undefined) {
    const geometry = this.userData.mesh.geometry;
    CurveObject3D.updateGeometryAttributes(geometry, curveConfig);
    geometry.computeBoundingSphere();

    this.setMaterialUniforms(
      CurveObject3D.configToUniforms(curveConfig, geometry.userData.curveLength, palette),
    );
  }

  private setMaterialUniforms(uniforms: CurveMaterialUnifroms) {
    const material = this.userData.mesh.material as ShaderMaterial;

    Object.entries(uniforms).forEach(([name, uniform]) => {
      if (material.uniforms[name] !== undefined) material.uniforms[name].value = uniform.value;
    });
  }

  static generateMaterial(curveConfig: CurveObjectConfig, precomputedLength?: number) {
    let curveLength = precomputedLength;
    if (curveLength === undefined || curveLength === 0) {
      const { curveLength: newLength } = CurveObject3D.getCurveGeometryAttributes(curveConfig);
      curveLength = newLength;
    }

    return new ShaderMaterial({
      vertexShader: standardVertexShader,
      fragmentShader: curveFragmentShader,
      uniforms: {
        color: {
          value: [1, 1, 1, curveConfig.opacity],
        },
        dashed: { value: curveConfig.dashed },
        soft: { value: curveConfig.soft },
        scale: { value: curveConfig.scale },
        thickness: { value: curveConfig.thickness },
        curveLength: { value: curveLength },
      },
      side: DoubleSide,
      transparent: true,
    });
  }

  static configToUniforms(
    curveConfig: CurveObjectConfig,
    curveLength: number,
    palette: Palette | undefined,
  ) {
    return {
      color: {
        value: hexToRgbaArray(
          resolvePaletteColor(curveConfig.color, palette),
          [1, 1, 1],
          curveConfig.opacity,
        ),
      },
      dashed: { value: curveConfig.dashed },
      soft: { value: curveConfig.soft },
      scale: { value: curveConfig.scale },
      thickness: { value: curveConfig.thickness },
      curveLength: { value: curveLength },
    } as CurveMaterialUnifroms;
  }

  static updateGeometryAttributes = (geometry: BufferGeometry, curveConfig: CurveObjectConfig) => {
    const { vertexPositions, index, uvs, curveLength } = CurveObject3D.getCurveGeometryAttributes(
      curveConfig,
    );

    geometry.setIndex(new BufferAttribute(new Uint16Array(index), 1));
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(vertexPositions), 3));
    geometry.setAttribute('uv', new BufferAttribute(new Float32Array(uvs), 2));

    geometry.userData.curveLength = curveLength;

    return geometry;
  };

  static getCurveGeometryAttributes(curveConfig: CurveObjectConfig) {
    const scale = new Vector3(curveConfig.scale[0], curveConfig.scale[1], 1);
    const points = curveConfig.points;
    const curvePath = curvePathFromPoints(points, scale);
    const vertexPositions: number[] = [];
    const index: number[] = [];
    const uvs: number[] = [];

    let indexOffset: number;

    let cumulativeCurveLength = 0;
    let cumulativeDivisionCount = 0;

    let curveLength: number;
    let curvePoints: Vector3[];
    let prevPoint: Vector3 | undefined;
    let nextPoint: Vector3 | undefined;

    curvePath.curves.forEach((curve, curveIndex) => {
      curveLength = curve.getLength();
      if (isNaN(curveLength)) curveLength = 1;

      if (curveIndex > 0 && curveIndex < curvePath.curves.length - 1) {
        // add two triangles between curve segments, to fill the void in corners with collapsed handles
        index.push(index[index.length - 3] + 1, index[index.length - 2], index[index.length - 3]);
        index.push(
          index[index.length - 3],
          index[index.length - 3 + 2],
          index[index.length - 3] + 1,
        );
      }

      const divisions = Math.round(Math.max(curveLength / 10, 5));
      curve.arcLengthDivisions = divisions;

      curvePoints = curve.getPoints(divisions);
      const simplifiedCurvePoints = simplifyCurvePoints(curvePoints, 0.05);

      simplifiedCurvePoints.forEach((curvePoint, pointIndex) => {
        prevPoint = pointIndex > 0 ? simplifiedCurvePoints[pointIndex - 1] : undefined;
        nextPoint =
          pointIndex < simplifiedCurvePoints.length - 1
            ? simplifiedCurvePoints[pointIndex + 1]
            : undefined;

        if (pointIndex > 0) {
          cumulativeCurveLength += simplifiedCurvePoints[pointIndex].distanceTo(
            simplifiedCurvePoints[pointIndex - 1],
          );
        }

        const normal = getVertexNormal(prevPoint, curvePoint, nextPoint);
        normal.multiplyScalar(curveConfig.thickness);

        vertexPositions.push(
          (curvePoint.x + normal.x) / scale.x,
          (curvePoint.y + normal.y) / scale.y,
          0,
        );
        vertexPositions.push(
          (curvePoint.x - normal.x) / scale.x,
          (curvePoint.y - normal.y) / scale.y,
          0,
        );

        uvs.push(cumulativeCurveLength, 1, cumulativeCurveLength, -1);
        indexOffset = (pointIndex - 1 + cumulativeDivisionCount) * 2;

        if (pointIndex > 0) {
          index.push(
            0 + indexOffset,
            1 + indexOffset,
            2 + indexOffset,
            3 + indexOffset,
            2 + indexOffset,
            1 + indexOffset,
          );
        }
      });

      cumulativeDivisionCount += simplifiedCurvePoints.length;
    });

    return { vertexPositions, index, uvs, curveLength: cumulativeCurveLength };
  }

  dispose() {
    const mesh = this.userData.mesh;
    (mesh?.material as ShaderMaterial)?.dispose();
    mesh?.geometry?.dispose();
  }
}

export default CurveObject3D;
